import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  useFilters, Panel, Loading, Empty, PageHeader, ExportBar, exportExcel, exportCSV,
  ScoreBadge, RiskBadge, Delta, BAND, bandOf, downloadPdf,
} from "./_shared";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { X, FileDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import CoachingReport from "@/components/CoachingReport";

const tip = { contentStyle: { borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 } };

function DetailDrawer({ userId, onClose }) {
  const [d, setD] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [reportSid, setReportSid] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/analytics/participants/${userId}`).then((r) => setD(r.data)).catch(() => onClose());
    api.get(`/admin/participants/${userId}/sessions`).then((r) => setSessions((r.data || []).filter((s) => s.status === "completed"))).catch(() => {});
  }, [userId]);

  const radar = d ? Object.entries(d.competencies).map(([competency, score]) => ({ competency, score: score || 0 })) : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="participant-drawer">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-stone-50 h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right">
        <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-display text-lg font-semibold text-stone-900">Participant Report</h2>
          <div className="flex items-center gap-2">
            {d && (
              <Button size="sm" variant="outline" data-testid="participant-pdf-btn"
                onClick={() => downloadPdf(`/analytics/participants/${userId}/report.pdf`, `${d.name}.pdf`).catch(() => toast.error("Download failed"))}
                className="rounded-md border-stone-300">
                <FileDown className="w-4 h-4 mr-1.5" /> PDF
              </Button>
            )}
            <button onClick={onClose} data-testid="close-drawer-btn" className="p-2 rounded-lg hover:bg-stone-100">
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>
        </div>

        {!d ? <Loading /> : (
          <div className="p-6 space-y-5">
            <div className="bg-white rounded-2xl border border-stone-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-xl font-semibold text-stone-900">{d.name}</div>
                  <div className="text-sm text-stone-500">{d.email}</div>
                  <div className="text-xs text-stone-500 mt-1">{d.department || "—"} • {d.designation || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-4xl font-semibold tabular-nums" style={{ color: BAND[bandOf(d.avg_score)].bg }}>{d.avg_score ?? "—"}</div>
                  <div className="text-[11px] text-stone-500 uppercase tracking-wide">Overall score</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 mt-4 text-center">
                <div><div className="text-lg font-semibold tabular-nums text-stone-900">{d.readiness}%</div><div className="text-[10px] text-stone-500 uppercase">Readiness</div></div>
                <div><div className="text-lg font-semibold tabular-nums text-stone-900">{d.scenarios_completed}/{d.scenarios_total}</div><div className="text-[10px] text-stone-500 uppercase">Completed</div></div>
                <div><div className="text-lg font-semibold tabular-nums text-stone-900">{d.duration_min}m</div><div className="text-[10px] text-stone-500 uppercase">Practice</div></div>
                <div><div className="text-sm font-semibold text-stone-900">{d.learning_agility}</div><div className="text-[10px] text-stone-500 uppercase">Agility</div></div>
              </div>
              {d.cert_ready && <div className="mt-3 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg px-3 py-1.5 inline-block">✓ Ready for certification</div>}
              {d.risk_flags.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{d.risk_flags.map((f) => <RiskBadge key={f} flag={f} />)}</div>}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Panel title="Competency Breakdown">
                <ResponsiveContainer width="100%" height={230}>
                  <RadarChart data={radar} outerRadius="70%">
                    <PolarGrid stroke="#e7e5e4" />
                    <PolarAngleAxis dataKey="competency" tick={{ fontSize: 9, fill: "#57534e" }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 8 }} stroke="#d6d3d1" />
                    <Radar dataKey="score" stroke="#b81f24" fill="#b81f24" fillOpacity={0.25} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="Progress Across Attempts">
                <ResponsiveContainer width="100%" height={230}>
                  <LineChart data={d.attempt_trend.map((t, i) => ({ ...t, n: `#${i + 1}` }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0eeec" />
                    <XAxis dataKey="n" tick={{ fontSize: 10 }} stroke="#a8a29e" />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#a8a29e" />
                    <Tooltip {...tip} />
                    <Line type="monotone" dataKey="score" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
            </div>

            <Panel title="Scenario Scores">
              <div className="space-y-2">
                {d.scenario_scores.map((s) => (
                  <div key={s.scenario} className="flex items-center justify-between text-sm">
                    <span className={`truncate pr-3 ${s.completed ? "text-stone-700" : "text-stone-400"}`}>{s.scenario}</span>
                    {s.completed ? <ScoreBadge value={s.score} /> : <span className="text-xs text-stone-400">Not done</span>}
                  </div>
                ))}
              </div>
            </Panel>

            <div className="grid sm:grid-cols-2 gap-4">
              <Panel title="Strengths">
                <ul className="space-y-1.5 text-sm text-stone-700">
                  {(d.strengths.length ? d.strengths : ["—"]).map((s, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">✓</span>{s}</li>)}
                </ul>
              </Panel>
              <Panel title="Development Areas">
                <ul className="space-y-1.5 text-sm text-stone-700">
                  {(d.improvements.length ? d.improvements : ["—"]).map((s, i) => <li key={i} className="flex gap-2"><span className="text-[#b81f24]">→</span>{s}</li>)}
                </ul>
              </Panel>
            </div>

            <Panel title="Coaching Style" right={<span className="text-sm font-semibold text-[#b81f24]">{d.coaching_style}</span>}>
              <p className="text-sm text-stone-600">{d.coaching_style_info?.explanation}</p>
              {d.coaching_style_info?.suggestions?.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm text-stone-700">
                  {d.coaching_style_info.suggestions.map((s, i) => <li key={i} className="flex gap-2"><span className="text-stone-400">•</span>{s}</li>)}
                </ul>
              )}
            </Panel>

            <Panel title="Recommended Practice" right={<Sparkles className="w-4 h-4 text-[#b45309]" />}>
              <div className="flex flex-wrap gap-2">
                {d.recommended_scenarios.length === 0 && <span className="text-sm text-stone-400">All scenarios completed 🎉</span>}
                {d.recommended_scenarios.map((s) => <span key={s} className="text-xs bg-stone-100 text-stone-700 rounded-md px-2.5 py-1.5">{s}</span>)}
              </div>
            </Panel>

            <Panel title="Manager Summary">
              <p className="text-sm text-stone-700 leading-relaxed">{d.manager_summary}</p>
            </Panel>

            <Panel title="Full Coaching Reports" right={<Sparkles className="w-4 h-4 text-[#b81f24]" />}>
              {sessions.length === 0 ? <p className="text-sm text-stone-400">No completed sessions.</p> : (
                <div className="space-y-2">
                  {sessions.map((s) => (
                    <div key={s.id} className="flex items-center justify-between text-sm border-b border-stone-100 pb-2">
                      <div className="min-w-0 pr-3">
                        <div className="text-stone-800 truncate">{s.scenario_title}</div>
                        <div className="text-xs text-stone-400">{(s.started_at || "").slice(0, 10)}{s.overall_score != null ? ` · ${s.overall_score}/100` : ""}</div>
                      </div>
                      <Button size="sm" variant="outline" data-testid={`view-coaching-report-${s.id}`}
                        onClick={() => setReportSid(s.id)} className="rounded-md border-stone-300 shrink-0">
                        View report
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        )}
      </div>

      {reportSid && (
        <div className="absolute inset-0 z-[60] flex justify-center items-start overflow-y-auto bg-stone-900/50 backdrop-blur-sm p-4 sm:p-8" data-testid="coaching-report-modal">
          <div className="relative w-full max-w-4xl bg-stone-50 rounded-2xl shadow-2xl my-4">
            <div className="sticky top-0 z-10 bg-white border-b border-stone-200 px-5 py-3 flex items-center justify-between rounded-t-2xl">
              <h3 className="font-display text-base font-semibold text-stone-900">AI Coaching Report</h3>
              <button onClick={() => setReportSid(null)} data-testid="close-coaching-modal-btn" className="p-2 rounded-lg hover:bg-stone-100">
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>
            <div className="p-5">
              <CoachingReport sessionId={reportSid} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ParticipantReports() {
  const { query } = useFilters();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/participants${query}`).then((r) => setRows(r.data.participants)).finally(() => setLoading(false));
  }, [query]);

  if (loading) return <Loading />;
  if (!rows || rows.length === 0) return <Empty />;

  const handleExcel = () =>
    exportExcel([{
      name: "Participants",
      rows: rows.map((r) => ({
        Name: r.name, Email: r.email, Department: r.department, Role: r.designation,
        Completed: `${r.scenarios_completed}/${r.scenarios_total}`, "Avg Score": r.avg_score,
        Readiness: r.readiness, "Cert Ready": r.cert_ready ? "Yes" : "No", Status: r.status,
        "Coaching Style": r.coaching_style, Flags: r.risk_flags.join("; "),
      })),
    }], "participants.xlsx");

  return (
    <div data-testid="participant-reports-page">
      <PageHeader title="Individual Participant Reports"
        description="Detailed competency, behaviour and readiness profile for every learner. Click a row to open the full report."
        right={<ExportBar onExcel={handleExcel} onCSV={() => exportCSV(rows, "participants.csv")} />} />

      <Panel testid="participant-table">
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b border-stone-200">
                <th className="py-3 px-2 font-medium">Participant</th>
                <th className="py-3 px-2 font-medium">Dept / Role</th>
                <th className="py-3 px-2 font-medium text-center">Progress</th>
                <th className="py-3 px-2 font-medium text-center">Score</th>
                <th className="py-3 px-2 font-medium text-center">Readiness</th>
                <th className="py-3 px-2 font-medium">Style</th>
                <th className="py-3 px-2 font-medium">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} onClick={() => setSelected(r.user_id)}
                  data-testid={`participant-row-${r.user_id}`}
                  className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors">
                  <td className="py-3 px-2">
                    <div className="font-medium text-stone-800">{r.name}</div>
                    <div className="text-xs text-stone-400">{r.email}</div>
                  </td>
                  <td className="py-3 px-2 text-stone-600 text-xs">{r.department || "—"}<br />{r.designation || "—"}</td>
                  <td className="py-3 px-2 text-center text-stone-600 tabular-nums">{r.scenarios_completed}/{r.scenarios_total}</td>
                  <td className="py-3 px-2 text-center"><ScoreBadge value={r.avg_score} /></td>
                  <td className="py-3 px-2 text-center">
                    <span className="tabular-nums text-stone-700">{r.readiness}%</span>
                    {r.cert_ready && <span className="ml-1 text-emerald-600">✓</span>}
                  </td>
                  <td className="py-3 px-2 text-stone-600 text-xs">{r.coaching_style}</td>
                  <td className="py-3 px-2">
                    <div className="flex flex-wrap gap-1">{r.risk_flags.slice(0, 2).map((f) => <RiskBadge key={f} flag={f} />)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {selected && <DetailDrawer userId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
